<!-- Copyright (c) 2023, Alix Mougenot (Au pied des Arbres), alix.mougenot@gmail.com -->
<!-- See license file. -->
<script setup lang="ts">
import type { Ref } from "vue";
import type { Actor, ActorCreation } from "@/types";
import ActorDisplay from "@/components/actors/ActorDisplay.vue";
import FormInput from "@/components/FormInput.vue";
import RelatedDossier from "@/components/dossier/RelatedDossier.vue";

import { ref, computed, watch, inject } from "vue";
import { makeSearchEngine, findDuplicate } from "@/logic/search";

import { MDBSwitch } from "mdb-vue-ui-kit";
import { actorsKey, createActorKey } from "@/logic/provide";
import { useRoute } from "vue-router";
import Navigation from "../navigation/Navigation.vue";
import {
  closeSecondaryViewKey,
  navigationKey,
  openSecondaryViewKey,
} from "../navigation/navigation";

export type ActorFilter = {
  withCases: boolean;
};

const offsetShift = 24 + 15 + 1; // 24px for the header, 15px for the padding, 1px for the border
const minifiedShift = -258.04; // panel height difference between minified and full view

const route = useRoute();
const allActors = inject(actorsKey);
const createActor = inject(createActorKey);

const openSecondaryView = inject(openSecondaryViewKey);
const closeSecondaryView = inject(closeSecondaryViewKey);
const currentNavKey = inject(navigationKey);

// Handle link into this page to setup the search filter
const searchInput = ref("");
const searchFilter: Ref<ActorFilter> = ref({
  withCases: false,
});

function updateSearchFromLink() {
  if (route.params.id) {
    const id = route.params.id as string;
    searchInput.value = id ? id : "";
    searchFilter.value.withCases = false;
  } else {
    searchInput.value = "";
    searchFilter.value.withCases = false;
  }
}
watch(route, updateSearchFromLink, { immediate: true, deep: true });

// Handles selected case
const _selectedActor = ref(null) as Ref<Actor | null>;
const panelX = ref(15 + offsetShift); // X placement of the event panel in the view

function actorSelected(selected: Actor, offset: number) {
  if (window.getSelection()?.toString()) {
    return;
  }

  if (_selectedActor.value && _selectedActor.value.id === selected.id) {
    _selectedActor.value = null;
  } else {
    // When another element is selected above it will shrink its panel and the global shift must be addapted
    var selectedIsAbove = false;
    for (let i = 0; i < currentActors.value.length; i++) {
      if (currentActors.value[i].id === _selectedActor.value?.id) {
        selectedIsAbove = true;
      }
      if (currentActors.value[i].id === selected.id) {
        break;
      }
    }
    const extraShift = selectedIsAbove ? minifiedShift : 0;
    _selectedActor.value = selected;
    panelX.value = offset + extraShift + offsetShift; // accouting for elements on top
  }
}

function deselect() {
  if (_selectedActor.value) {
    _selectedActor.value = null;
  }
}

const currentActor = computed(() => _selectedActor.value);

// Hanlde filtering
const actors = computed(() => {
  if (!allActors) {
    return [];
  }
  return allActors.value.list.filter(
    (d: Actor) => d.type !== "Centre de Santé" && !d.institution_finess
  );
});

// search (part of filter)
const { searchResult } = makeSearchEngine(
  {
    includeScore: true,
    ignoreLocation: true,
    findAllMatches: true,
    minMatchCharLength: 3,
    keys: [
      { name: "id" },
      { name: "first_name", weight: 2 },
      { name: "last_name", weight: 3 },
      { name: "institution_name", weight: 3 },
      {
        name: "full_name",
        getFn: (actor: Actor) =>
          actor.institution_name
            ? actor.institution_name
            : actor.first_name + " " + actor.last_name,
        weight: 5,
      },
      {
        name: "full_name_inverted",
        getFn: (actor: Actor) =>
          actor.institution_name
            ? actor.institution_name
            : actor.last_name + " " + actor.first_name,
        weight: 4,
      },
      "email",
      "notes",
    ],
  },
  searchInput,
  actors
);

watch(
  searchResult,
  () => {
    // Auto open singled-out searches
    if (searchResult.value.length === 1) {
      _selectedActor.value = searchResult.value[0] as Actor;
    } else {
      _selectedActor.value = null;
    }
  },
  { immediate: true }
);

const filteredActors = computed(() => {
  const source = searchResult.value?.length
    ? (searchResult.value as Array<Actor>)
    : actors.value;

  return source.filter(
    (act) => !searchFilter.value.withCases || act.cases.length
  );
});

const currentPage: Ref<number> = ref(0);
const maxPage = computed(() => Math.ceil(filteredActors.value.length / 20) - 1);
const currentActors = computed(() => {
  return filteredActors.value.slice(0, (currentPage.value + 1) * 20);
});
const loadMoreActors = () => {
  if (currentPage.value < maxPage.value) {
    currentPage.value += 1;
  }
};

watch(searchFilter, deselect, { deep: true });
watch(currentActors, () => {
  if (currentActor.value) {
    if (
      currentActors.value.find((a) => a.id === currentActor.value?.id) ===
      undefined
    ) {
      deselect();
    }
  }
});

// Handle creation
const isCreatingActor = ref(false);
const actorTemplate: Actor = {
  id: "",
  type: "Patient",
  title: "",
  first_name: "",
  last_name: "",
  notes: "",
  contact: {
    email: "",
    address: "",
    zipcode: "",
    city: "",
    phone_landline: "",
    phone_cellular: "",
  },
  institution_name: null,
  institution_finess: null,
  cases: [],
};

const actorCreationTemplate = ref<Actor>({ ...actorTemplate });

const potentialDuplicates = ref<Actor[]>([]);

const onBeginActorCreation = () => {
  deselect();
  panelX.value = offsetShift;
  isCreatingActor.value = true;
  searchInput.value = "";
  actorCreationTemplate.value = { ...actorTemplate };
  if (openSecondaryView && currentNavKey) {
    openSecondaryView(currentNavKey);
  }
};

function onCancelActorCreation() {
  actorCreationTemplate.value = { ...actorTemplate };
  searchInput.value = "";
  isCreatingActor.value = false;
  potentialDuplicates.value = [];
  if (closeSecondaryView && currentNavKey) {
    closeSecondaryView(currentNavKey);
  }
}

function onCreateActor(actorCreation: ActorCreation) {
  if (createActor) {
    createActor(actorCreation);
  } else {
    console.error("No createActor function provided");
  }
  isCreatingActor.value = false;
  searchInput.value = "";
}

function onNameUpdate(info: Actor) {
  if (isCreatingActor.value) {
    if (allActors?.value.list) {
      potentialDuplicates.value = findDuplicate(allActors?.value.list, info);
    }
  }
}
</script>

<template>
  <Teleport to="#toolbar">
    <span>Recherche dans :</span>
    <div>
      <MDBSwitch v-model="searchFilter.withCases" label="Acteur d'un dossier" />
    </div>
  </Teleport>
  <Teleport to="#searchbar">
    <FormInput v-model="searchInput" label="Recherche" type="text" />
  </Teleport>
  <div class="actors">
    <div class="container-actrs">
      <div class="container-stats">
        <span>{{ filteredActors.length }}</span>
        <span v-if="filteredActors.length <= 1">
          {{ " requérant " }}
        </span>
        <span v-else>
          {{ " requérants" }}
        </span>
      </div>
      <div class="container-listing">
        <div v-if="!isCreatingActor">
          <h3>Fichier des requérants</h3>
          <span>
            Ce fichier contient les requérants et tout autres acteurs liés à un
            dossier.</span
          ><br />
          <span
            >Les institutions, prothésistes, et étudiants praticiens sont à
            ajouter à ce fichier avant d'être liés à un dossier.
          </span>
        </div>
        <div v-if="isCreatingActor">
          <ActorDisplay
            :modelValue="actorCreationTemplate"
            openMode="Create"
            @click:create="onCreateActor"
            @click:cancel="onCancelActorCreation"
            @update:info="onNameUpdate"
          />
        </div>
        <div v-else>
          <span class="button" @click.stop="onBeginActorCreation"
            >Ajouter un requérant à la base</span
          >
        </div>
        <div v-if="!filteredActors || filteredActors.length === 0">
          Aucun acteur trouvé.
        </div>
        <template v-if="!isCreatingActor">
          <template v-for="actor of currentActors" :key="actor.id">
            <ActorDisplay
              :class="{
                hide: currentActor && currentActor.id !== actor.id,
                selected: currentActor && currentActor.id === actor.id,
              }"
              :modelValue="actor"
              openMode="Edit"
              :minified="!currentActor || currentActor.id !== actor.id"
              @click:detail="actorSelected"
            />
          </template>
          <div v-if="currentPage < maxPage" class="center">
            <span class="button" @click="loadMoreActors">Montrer plus</span>
          </div>
        </template>
      </div>
    </div>
    <Navigation nav-key="Dossiers du requérant">
      <RelatedDossier
        :entity="currentActor"
        :panel-x="panelX"
        v-if="currentActor !== null"
      />
      <template v-if="isCreatingActor">
        <div class="sim-list" :style="{ 'padding-top': panelX + 'px' }">
          <div class="sidecar">
            <div class="header-container">
              <h2>Doublons potentiels</h2>
            </div>
            <div class="container-actrs">
              <div class="container-listing">
                <span v-if="potentialDuplicates.length === 0"
                  >Aucun doublon trouvé</span
                >
                <template v-for="actor in potentialDuplicates" :key="actor.id">
                  <ActorDisplay openMode="ReadOnly" :modelValue="actor" />
                </template>
              </div>
            </div>
          </div>
        </div>
      </template>
    </Navigation>
  </div>
</template>

<style scoped lang="scss">
.center {
  align-items: center;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-evenly;
}
.actors {
  display: flex;
  flex-direction: row;
  column-gap: 3em;

  .group {
    display: flex;
    flex-direction: row;

    &.split {
      justify-content: space-between;
    }
  }

  .hide {
    opacity: 0.2;
  }
}

.container-actrs {
  display: flex;
  flex-direction: column;
  padding-top: 1em;
  padding-left: 1em;
  margin-right: 1em;

  .container-stats {
    text-align: right;
  }
}

.container-listing {
  display: flex;
  flex-direction: column;
  padding-top: 1em;
  row-gap: 4em;
  margin-bottom: 100px;
}

.sim-list {
  padding-left: 2em;

  .header-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 56px;
    min-width: 680px;

    border-bottom-width: 6px;
    border-bottom-style: solid;
    border-color: inherit;
    white-space: nowrap;

    h2 {
      color: inherit;
    }
  }
}
</style>
